<script setup lang="ts">
import { computed, watch, inject, ref } from 'vue'
import { useOpsStore } from '@/stores/ops'
import { useRadioLogStore } from '@/stores/radioLog'
import { useAssignmentsStore } from '@/stores/assignments'
import { useTeamsStore } from '@/stores/teams'
import { useIwiStore } from '@/stores/iwi'
import { usePriorityTrafficStore } from '@/stores/priorityTraffic'
import { usePermissionsStore } from '@/stores/permissions'
import { useCaltopoStore } from '@/stores/caltopo'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useToastStore } from '@/stores/toast'
import { MessageType, AssignmentStatus } from '@/types'
import LogEntryForm from '@/components/comms/LogEntryForm.vue'
import LogTable from '@/components/comms/LogTable.vue'
import IwiPanel from '@/components/comms/IwiPanel.vue'
import PriorityTrafficPanel from '@/components/comms/PriorityTrafficPanel.vue'
import CommsConfigPanel from '@/components/comms/CommsConfigPanel.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const incidentId = inject<string>('incidentId')!
const opsStore = useOpsStore()
const radioLogStore = useRadioLogStore()
const assignmentsStore = useAssignmentsStore()
const teamsStore = useTeamsStore()
const iwiStore = useIwiStore()
const ptiStore = usePriorityTrafficStore()
const permissionsStore = usePermissionsStore()
const caltopoStore = useCaltopoStore()
const { opRole } = useCurrentOpRole()
const toast = useToastStore()

const searchQuery = ref('')

const selectedOpId = computed(() => opsStore.selectedOpId)
const selectedOp = computed(() => opsStore.selectedOp)

const canLogEntry = computed(() => permissionsStore.can('radio_log.log_entry', opRole.value))
const canEditEntry = computed(() => permissionsStore.can('radio_log.edit_entry', opRole.value))
const canDeleteEntry = computed(() => permissionsStore.can('radio_log.delete_entry', opRole.value))
const canExport = computed(() => permissionsStore.can('radio_log.export_ics309', opRole.value))
const canDeclareIwi = computed(() => permissionsStore.can('iwi.declare', opRole.value))
const canCreatePt = computed(() => permissionsStore.can('priority_traffic.create', opRole.value))

// Most recent EmergencyTraffic entry — used to populate the Declare IWI banner
const pendingEmergencyEntry = computed(() => {
  if (iwiStore.activeIwi) return null
  return (
    radioLogStore.entries.find(
      (e) => e.message_type === MessageType.EmergencyTraffic && !iwiStore.usedEntryIds.has(e.entry_id),
    ) ?? null
  )
})

// Most recent PriorityTraffic entry with no linked PTI
const pendingPtEntry = computed(() => {
  if (ptiStore.activePti) return null
  return (
    radioLogStore.entries.find(
      (e) => e.message_type === MessageType.PriorityTraffic && !ptiStore.usedEntryIds.has(e.entry_id),
    ) ?? null
  )
})

watch(
  selectedOpId,
  (opId) => {
    if (opId) {
      assignmentsStore.subscribeToAssignments(incidentId, opId)
    }
  },
  { immediate: true },
)

function handleCommsTransition(assignmentId: string, status: AssignmentStatus) {
  const op = opsStore.selectedOp
  if (!op?.caltopo_map_id) return
  const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === assignmentId)
  if (!assignment?.caltopo_assignment_id) return
  caltopoStore.writeBack(assignment.caltopo_assignment_id, op.caltopo_map_id, { assignment_status: status })
}

async function handleDelete(entryId: string) {
  if (!selectedOpId.value) return
  try {
    await radioLogStore.deleteEntry(incidentId, selectedOpId.value, entryId)
  } catch {
    toast.add('Failed to delete entry', 'error')
  }
}

async function handleExport() {
  if (!selectedOpId.value) return
  toast.add('ICS-309 export coming soon', 'default')
}

async function createPt(fromEntry: { team_identifier: string; team_id: string | null; entry_id: string }) {
  if (!selectedOpId.value) return

  const matchedTeam = teamsStore.teams.find((t) => t.team_identifier === fromEntry.team_identifier)
  if (!matchedTeam && !fromEntry.team_id) {
    toast.add('Could not find team — check team identifier', 'error')
    return
  }

  try {
    await ptiStore.createPti(incidentId, selectedOpId.value, {
      declared_by_team_id: fromEntry.team_id ?? matchedTeam?.team_id ?? '',
      declared_by_team_identifier: fromEntry.team_identifier,
      triggering_radio_log_entry_id: fromEntry.entry_id,
    })
  } catch {
    toast.add('Failed to create priority traffic incident', 'error')
  }
}

async function declareIwi(fromEntry: { team_identifier: string; team_id: string | null; entry_id: string }) {
  if (!selectedOpId.value) return

  const matchedTeam = teamsStore.teams.find((t) => t.team_identifier === fromEntry.team_identifier)
  if (!matchedTeam && !fromEntry.team_id) {
    toast.add('Could not find team — check team identifier', 'error')
    return
  }

  try {
    await iwiStore.createIwi(incidentId, selectedOpId.value, {
      declared_by_team_id: fromEntry.team_id ?? matchedTeam?.team_id ?? '',
      declared_by_team_identifier: fromEntry.team_identifier,
      triggering_radio_log_entry_id: fromEntry.entry_id,
    })
  } catch {
    toast.add('Failed to declare IWI', 'error')
  }
}
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
      <div>
        <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Radio Log</h2>
        <p class="font-mono text-xs text-outline mt-0.5">
          <span v-if="selectedOp">OP {{ selectedOp.op_number }} — {{ selectedOp.date }}</span>
          <span v-else>No operational period selected</span>
          <span class="ml-2">· {{ radioLogStore.entries.length }} entr{{ radioLogStore.entries.length !== 1 ? 'ies' : 'y' }}</span>
        </p>
      </div>
      <PermissionGuard action="radio_log.export_ics309" :role="opRole">
        <FosButton variant="secondary" @click="handleExport">
          Export ICS-309
        </FosButton>
      </PermissionGuard>
    </div>

    <!-- No OP selected -->
    <div v-if="!selectedOpId" class="flex-1 flex items-center justify-center text-outline">
      <div class="text-center">
        <span class="material-symbols-outlined text-4xl mb-3 block">radio</span>
        <p class="font-mono text-xs uppercase tracking-widest">No operational period selected.</p>
      </div>
    </div>

    <!-- Main content — two-column when IWI active -->
    <template v-else>
      <!-- Declare IWI banner — shown when EmergencyTraffic logged with no active IWI -->
      <div
        v-if="pendingEmergencyEntry && canDeclareIwi"
        class="mx-6 mt-4 flex items-center justify-between gap-4 bg-red-50 dark:bg-red-950/30 border border-red-400 rounded px-4 py-3"
      >
        <div class="flex items-center gap-2.5">
          <span class="w-2 h-2 rounded-full bg-red-500 shrink-0" />
          <div>
            <p class="font-mono text-xs font-bold text-red-700 dark:text-red-400 uppercase tracking-wider">
              Emergency Traffic — Team {{ pendingEmergencyEntry.team_identifier }}
            </p>
            <p class="font-mono text-[0.65rem] text-red-600/70 dark:text-red-500/70 mt-0.5">
              Confirm to declare an IWI and activate the emergency response protocol.
            </p>
          </div>
        </div>
        <FosButton
          variant="primary"
          size="sm"
          class="!bg-red-600 hover:!brightness-110 shrink-0"
          @click="declareIwi(pendingEmergencyEntry)"
        >
          Declare IWI
        </FosButton>
      </div>

      <!-- Priority Traffic banner — shown when PriorityTraffic logged with no active PTI -->
      <div
        v-if="pendingPtEntry && canCreatePt"
        class="mx-6 mt-4 flex items-center justify-between gap-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-400 rounded px-4 py-3"
      >
        <div class="flex items-center gap-2.5">
          <span class="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
          <div>
            <p class="font-mono text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
              Priority Traffic — Team {{ pendingPtEntry.team_identifier }}
              <span v-if="pendingPtEntry.priority_traffic_plan" class="ml-1">— Plan {{ pendingPtEntry.priority_traffic_plan }}</span>
            </p>
            <p class="font-mono text-[0.65rem] text-amber-600/70 dark:text-amber-500/70 mt-0.5">
              Confirm to open a priority traffic incident and record location and resources.
            </p>
          </div>
        </div>
        <FosButton
          variant="primary"
          size="sm"
          class="!bg-amber-500 hover:!brightness-110 shrink-0"
          @click="createPt(pendingPtEntry)"
        >
          Create Find
        </FosButton>
      </div>

      <!-- Comms config: ICP phone + radio channels -->
      <CommsConfigPanel
        v-if="selectedOpId"
        :incident-id="incidentId"
        :op-id="selectedOpId"
      />

      <!-- Search -->
      <div class="px-6 pt-4 shrink-0">
        <div class="flex border border-border rounded overflow-visible">
          <FosInput
            v-model="searchQuery"
            placeholder="Search log entries…"
            class="flex-1 border-0 rounded-none"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="flex items-center justify-center px-2 bg-surface-container-high hover:bg-surface-container-highest border-l border-border text-outline hover:text-on-surface transition-colors"
            @click="searchQuery = ''"
          >
            <span class="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
      </div>

      <!-- Entry form -->
      <PermissionGuard action="radio_log.log_entry" :role="opRole">
        <div class="px-6 pt-3 shrink-0">
          <LogEntryForm :incident-id="incidentId" :op-id="selectedOpId" @comms-transition="handleCommsTransition" />
        </div>
      </PermissionGuard>

      <!-- Log + panels layout; panels take 320px each when active -->
      <div
        class="flex-1 overflow-hidden flex gap-0 mt-4"
        :class="{
          'grid grid-cols-[1fr_320px_320px]': iwiStore.activeIwi && ptiStore.activePti,
          'grid grid-cols-[1fr_320px]': (iwiStore.activeIwi || ptiStore.activePti) && !(iwiStore.activeIwi && ptiStore.activePti),
        }"
      >
        <!-- Log table -->
        <div class="overflow-y-auto px-6 pb-6 min-w-0 flex-1">
          <LogTable
            :incident-id="incidentId"
            :op-id="selectedOpId"
            :can-edit="canEditEntry"
            :can-delete="canDeleteEntry"
            :permission-role="opRole"
            :search-query="searchQuery"
            @delete="handleDelete"
          />
        </div>

        <!-- IWI panel -->
        <IwiPanel
          v-if="iwiStore.activeIwi"
          :incident-id="incidentId"
          :op-id="selectedOpId"
        />

        <!-- Priority Traffic panel -->
        <PriorityTrafficPanel
          v-if="ptiStore.activePti"
          :incident-id="incidentId"
          :op-id="selectedOpId"
        />
      </div>
    </template>
  </div>
</template>
